import { Code } from '@connectrpc/connect'
import type { BusinessMaster } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import type { BusinessMasterRow } from '~/hooks/businessMaster/useListBusinessMasters'
import { entitySourceOf, useEditModalForm } from '~/hooks/useEditModalForm'
import {
  civilDateToProtoDate,
  protoDateToCivilDate,
  todayCivilDate,
} from '~/models/civilDate'
import { BusinessMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/components/BusinessMasterFormFields'
import { useGetBusinessMaster } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/hooks/useGetBusinessMaster'
import { useUpdateBusinessMaster } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/hooks/useUpdateBusinessMaster'
import {
  BusinessMasterFormSchema,
  type BusinessMasterFormValues,
} from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/models/schema'
import { resolveBusinessMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/BusinessMaster/models/serverError'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'

type Props = {
  isOpen: boolean
  row: BusinessMasterRow
  onClose: () => void
}

const BUSINESS_MASTER_EDIT_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.AlreadyExists]:
    'page:adminOcctoMaster.businessMaster.serverError.alreadyExists',
  [Code.Aborted]: 'page:adminOcctoMaster.businessMaster.serverError.aborted',
  [Code.NotFound]: 'page:adminOcctoMaster.businessMaster.serverError.notFound',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.businessMaster.serverError.invalidArgument',
}

const toFormValues = (fetched: BusinessMaster): BusinessMasterFormValues => {
  return {
    businessCode: fetched.businessCode,
    businessName: fetched.businessName,
    licenseTypes: fetched.licenseTypes,
    purpose: fetched.purpose,
    validFrom: protoDateToCivilDate(fetched.validFrom) ?? todayCivilDate(),
    validTo: protoDateToCivilDate(fetched.validTo),
    registrationNumber: fetched.registrationNumber ?? '',
  }
}

/**
 * 編集モーダル。
 *
 * `useEditModalForm` + `CrudModalLayout` に集約 (他 7 タブと同 pattern)。
 * useGetBusinessMaster で full entity を取り直してから form を populate する。
 *
 * businessCode は readonly (AC-006)。 監査情報 (AuditPanel) は Phase 1 では
 * 非表示 (issue #3221)。
 */
export const EditBusinessMasterModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateBusinessMaster()
  const getQuery = useGetBusinessMaster(row.id)
  const { isError, error } = getQuery

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    isDirty,
    loadStatus,
  } = useEditModalForm<BusinessMaster, BusinessMasterFormValues>({
    source: entitySourceOf(getQuery, (d) => d.businessMaster),
    toFormValues,
    schema: BusinessMasterFormSchema,
    defaultValues: {
      businessCode: '',
      businessName: '',
      licenseTypes: [],
      purpose: 1, // BusinessPurpose.NORMAL
      validFrom: todayCivilDate(),
      validTo: undefined,
      registrationNumber: '',
    },
    onSubmit: async (values, { expectedUpdatedAt }) => {
      await mutateAsync({
        id: row.id,
        businessName: values.businessName,
        licenseTypes: values.licenseTypes,
        purpose: values.purpose,
        validFrom: civilDateToProtoDate(values.validFrom),
        validTo:
          values.validTo !== undefined
            ? civilDateToProtoDate(values.validTo)
            : undefined,
        expectedUpdatedAt,
        registrationNumber:
          values.registrationNumber !== ''
            ? values.registrationNumber
            : undefined,
      })
    },
    onClose,
    customErrorKeys: BUSINESS_MASTER_EDIT_ERROR_KEYS,
    unknownErrorKey: 'page:adminOcctoMaster.businessMaster.serverError.unknown',
    resolveDetailedError: resolveBusinessMasterDetailedError,
  })

  return (
    <CrudModalLayout
      handleSave={handleSave}
      // 入力途中で閉じようとした時に離脱確認ダイアログを出す。 取得値の反映は
      // reset() で defaultValues ごと差し替わるため dirty にならない。
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      loadError={
        isError ? (
          <Alert
            status="error"
            title={mapMasterGetError(
              t,
              error,
              'page:adminOcctoMaster.businessMaster.serverError',
            )}
          />
        ) : undefined
      }
      loadStatus={loadStatus}
      onClose={handleClose}
      serverError={serverError}
      title={t('page:adminOcctoMaster.businessMaster.modal.editTitle')}
    >
      <BusinessMasterFormFields control={control} errors={errors} isEdit />
    </CrudModalLayout>
  )
}
